import { observable, runInAction } from 'mobx'
import type { Location, NavigateFunction, RouteObject } from 'react-router-dom'
import { NavigationType } from 'react-router-dom'
import { openInNewTab } from '../utils/window/openInNewTab'
import { getLogger } from './logger/logger.service'
import type { BasePagesEnum, BaseSubPageKey, PageList } from './models/pagelist-models'
import { occService } from './occ.service'

const logger = getLogger('NavigationService')

export abstract class NavigationService<PagesEnum extends BasePagesEnum, SubPageKey extends BaseSubPageKey<PagesEnum>> {
    protected abstract getOverrideSiteIdPages(): Array<keyof PagesEnum>

    protected abstract getPages(): PageList<PagesEnum, SubPageKey>

    public get Pages() {
        return this.getPages()
    }

    private readonly pagesEnum: PagesEnum
    public get PagesEnum() {
        return this.pagesEnum
    }

    public constructor(pagesEnum: PagesEnum) {
        this.pagesEnum = pagesEnum
    }

    // Routes storage
    private routes: RouteObject[] = []

    public setRoutes(r: RouteObject[]) {
        this.routes = r
    }

    public getRoutes(): RouteObject[] {
        return this.routes
    }

    private navigate: NavigateFunction

    public setNavigate(navigate: NavigateFunction) {
        this.navigate = navigate
        window.navigate = navigate
    }

    private location: Location

    public setLocation(navigate: Location) {
        this.location = navigate
    }

    public getLocation() {
        return this.location
    }

    public pageObs = observable.box<keyof PagesEnum>(undefined)

    // Detects if current page was reached by a browser back button
    private navigationType: NavigationType

    public setCurrentPage(page: keyof PagesEnum) {
        logger.value.debug('reset goBackInProgress', Date.now() - window.loadMs)
        this.navigationInProgress = false
        runInAction(() => {
            this.pageObs.set(page)

            if (this.getOverrideSiteIdPages().includes(page)) {
                occService.setOverrideSiteId(undefined)
            }
        })

        return true
    }

    public getCurrentPage = () => this.pageObs.get()

    public setNavigationType(navigationType: NavigationType) {
        this.navigationType = navigationType
    }

    /**
     * Checks if the navigation was performed using the browser's back/forwards buttons
     */
    public navigatedByBrowserButtons = (): boolean => this.navigationType === NavigationType.Pop

    /**
     * Go to the provided page ur by PageEnum
     * <br/>
     * > Be cautious when navigating to pages with default subpages, as this can
     * cause the parent page to redirect and affect the navigation history. This
     * may result in difficulties when trying to navigate back in the browser,
     * giving the impression that the user is unable to go back.
     * */
    public goToPage(page: keyof PagesEnum, params?: Record<string, string>, keepQueryParams = false, subPage?: string) {
        setTimeout(() => {
            logger.value.trace('goToPage', {
                page,
                params: this.getQueryParams(params ?? {}),
                keepQueryParams,
                subPage,
            })
            const url = this.getPageUrl(page, params, keepQueryParams, subPage)
            this.navigate(url)
        }, 10)
    }

    private getPageOrSubpage(page: keyof PagesEnum, subPage?: string) {
        let pageOrSubpage = this.getPages()[page]
        if (!!subPage && subPage.length) {
            const subPageElement = pageOrSubpage?.subPages?.find(subP => (subP?.alias ?? '').toString() === subPage)
            if (!!subPageElement && subPageElement.urls && subPageElement.urls.length && !!subPageElement.urls[0]) {
                pageOrSubpage = subPageElement
            }
        }

        return pageOrSubpage
    }

    /**
     * Returns all the URLs associated with the page
     * @param page Page type from PagesEnum
     * @param subPage Alias of subpage
     */
    public getPageUrls(page: keyof PagesEnum, subPage?: string): string[] {
        const pageOrSubpage = this.getPageOrSubpage(page, subPage)
        return pageOrSubpage?.urls ?? []
    }

    /**
     *
     * @param page Page type from PagesEnum
     * @param params Query parameters (they become URL parameters if defined on the Page)
     * @param keepQueryParams Append all query parameters from the current URL
     * @param subPage Alias of subpage
     * @returns URL for the requested page
     */
    // eslint-disable-next-line complexity -- todo
    public getPageUrl(
        page: keyof PagesEnum,
        params?: { [key: string]: string } | null,
        keepQueryParams = false,
        subPage?: string
    ) {
        const pageOrSubpage = this.getPageOrSubpage(page, subPage)

        /*
         * The "urls" property is undefined if a react router page does not have a corresponding umbraco page setup.
         * Hence, if this is the case return an empty string to prevent UI from breaking.
         * */
        let url = pageOrSubpage.urls && pageOrSubpage.urls.length ? pageOrSubpage.urls[0] : ''
        if (!url) return url

        if (keepQueryParams) {
            const query = new URLSearchParams(this.location.search)
            query.forEach((value, key) => {
                if (!params) params = {}
                params[key] = value
            })
        }

        if (params) {
            let index = 0
            for (const key in params) {
                if (pageOrSubpage.params && pageOrSubpage.params.includes(key)) {
                    url = url.replace(':' + key, params[key])
                } else {
                    url += index === 0 ? '?' : '&'
                    url += `${key}=${params[key]}`
                    index++
                }
            }
        }

        return url
    }

    public getQueryParams(params: { [key: string]: string }) {
        let url = ''

        if (params) {
            let index = 0
            for (const key in params) {
                url += index === 0 ? '?' : '&'
                url += `${key}=${params[key]}`
                index++
            }
        }

        return url
    }

    public goTo(url: string, newTab: boolean = false) {
        logger.value.trace('goTo', { url, newTab })

        if (this.isRouted(url)) {
            if (newTab) {
                // eslint-disable-next-line no-restricted-properties -- we can use location.origin for tests
                const origin: string = url.slice(0, 0) === '/' ? document.location.origin : ''
                openInNewTab(origin + url)
            } else {
                setTimeout(() => {
                    this.navigate(url)
                }, 10)
            }
        } else {
            if (newTab) {
                openInNewTab(`${url}`)
            } else {
                // eslint-disable-next-line no-restricted-properties -- ideally the only place we allow navigation outside of react-router
                document.location.assign(url)
            }
        }
    }

    public goToHome() {
        logger.value.trace('goToHome')
        this.goTo('/')
    }

    private navigationInProgress = false

    public canNavigate() {
        return !this.navigationInProgress
    }

    public goBack() {
        logger.value.trace('goBack', { navigationInProgress: this.navigationInProgress })
        if (!this.navigationInProgress) {
            this.navigationInProgress = true
            this.navigate(-1)
        }
    }

    public isRouted(url: string) {
        // eslint-disable-next-line no-restricted-properties -- we can use location.origin for tests
        return url.startsWith('/') || url.startsWith(document.location.origin)
    }

    private previousPage: keyof PagesEnum

    public setPreviousPage(page: keyof PagesEnum) {
        this.previousPage = page
    }

    public getPreviousPage() {
        return this.previousPage
    }

    public PagesNames = (includeChildPages = false): Array<keyof PagesEnum> => {
        const pages: Array<keyof PagesEnum> = []
        for (const enumKey in this.PagesEnum) {
            const key = enumKey as keyof PagesEnum
            const page = this.Pages[key]
            if (includeChildPages || !page.parent) pages.push(key)
        }
        return pages
    }

    // public PagesNames = (includeChildPages = false, includeSubPages = false): Array<PagesEnum | SubPageKey> => {
    //     const pages: Array<PagesEnum | SubPageKey> = []
    //     for (const enumKey of this.PagesEnum) {
    //         const key = enumKey as PagesEnum
    //         const page = this.Pages[key]
    //         if (includeChildPages || !page.parent) pages.push(key)

    //         if (includeSubPages && page?.subPages?.length) {
    //             page.subPages?.forEach(subPage => pages.push(subPage.alias!))
    //         }
    //     }
    //     return pages
    // }
}
